/**
 * Outbox queue for feedback submissions that hit a transient failure
 * (network, 5xx, lexserver unavailable).
 *
 * Persistence: LocalStorage so a tab close/refresh doesn't lose pending
 * records. FIFO eviction at 500 items keeps quota bounded.
 *
 * Retry: useFeedback hook drives flush — see hooks/useFeedback.ts.
 */

import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

/**
 * Shape of a queued feedback submission. Mirrors `CreateFeedbackInput` on
 * the server (lexserver/src/db/models/user-feedback.ts:168) — keep these
 * fields aligned when the contract changes.
 */
export interface QueuedFeedback {
  /** Client-generated id so retries don't create duplicates server-side. */
  clientId: string;
  /** Number of failed attempts so far (informational). */
  attempts: number;
  /** Last attempt timestamp (ms epoch). */
  lastAttemptAt: number | null;
  /** The full submission payload to POST to /api/feedback. */
  payload: {
    polarity: "positive" | "negative" | "neutral";
    comment?: string;
    surface: string;
    surfaceContext: Record<string, unknown>;
    clientMeta: {
      appVersion: string;
      userAgent: string;
      locale: string;
      viewport?: { width: number; height: number };
      timestamp: string; // ISO; coerced to Date by the server
    };
  };
}

const MAX_QUEUE_SIZE = 500;

interface FeedbackStoreState {
  outbox: QueuedFeedback[];
  isFlushing: boolean;

  enqueue: (item: QueuedFeedback) => void;
  /** Remove a successfully-sent item by clientId. */
  ack: (clientId: string) => void;
  /** Bump attempts + lastAttemptAt for a still-failing item. */
  touch: (clientId: string) => void;
  setFlushing: (flushing: boolean) => void;
  /** Drop everything (debug + sign-out cleanup). */
  clear: () => void;
}

export const useFeedbackStore = create<FeedbackStoreState>()(
  persist(
    (set) => ({
      outbox: [],
      isFlushing: false,

      enqueue: (item) =>
        set((s) => {
          const existing = s.outbox.findIndex((q) => q.clientId === item.clientId);
          if (existing >= 0) {
            // Don't double-enqueue the same clientId — refresh in place.
            const next = s.outbox.slice();
            next[existing] = item;
            return { outbox: next };
          }
          // FIFO eviction: drop oldest when over the cap.
          const next = s.outbox.length >= MAX_QUEUE_SIZE
            ? [...s.outbox.slice(s.outbox.length - MAX_QUEUE_SIZE + 1), item]
            : [...s.outbox, item];
          return { outbox: next };
        }),

      ack: (clientId) =>
        set((s) => ({ outbox: s.outbox.filter((q) => q.clientId !== clientId) })),

      touch: (clientId) =>
        set((s) => ({
          outbox: s.outbox.map((q) =>
            q.clientId === clientId
              ? { ...q, attempts: q.attempts + 1, lastAttemptAt: Date.now() }
              : q,
          ),
        })),

      setFlushing: (flushing) => set({ isFlushing: flushing }),
      clear: () => set({ outbox: [], isFlushing: false }),
    }),
    {
      name: "lexide-feedback-outbox",
      storage: createJSONStorage(() => localStorage),
      // Don't persist isFlushing — it's runtime only.
      partialize: (s) => ({ outbox: s.outbox }),
    },
  ),
);
